import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '@/api/client';
import { auth, wallet as walletApi } from '@/api/endpoints';
import { useSession } from '@/store/session';
import { Icon, type IconName } from '@/components/ui/Icon';
import {
  Button, Empty, ErrorNote, Loading, Meter, Panel, Row, ScreenBody, ScreenHeader, Stat, StatGrid,
} from '@/components/ui/primitives';

type Withdrawals = Awaited<ReturnType<typeof walletApi.withdrawals>>;
type Profile = Awaited<ReturnType<typeof auth.payoutProfile.get>>;

/**
 * Withdraw Shwe Kyat.
 *
 * Two gates the UI has to make legible rather than hide:
 *   1. The payment profile is a ONE-TIME write. Unlimited edits is how payout
 *      fraud works, so once saved it is locked and changing it needs support.
 *   2. Eligibility is server-decided (linked account, verified phone, age, risk
 *      score). We surface the server's reason verbatim instead of guessing.
 *
 * Note the balance never drops on request — the amount moves into `kyat_locked`,
 * so the player can see the funds are still theirs but no longer spendable.
 */
export function Withdraw({ onBack }: { onBack: () => void }) {
  const [data, setData] = useState<Withdrawals | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [amount, setAmount] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const balance = useSession((s) => s.wallet);
  const flags = useSession((s) => s.flags);
  const applyWallet = useSession((s) => s.applyWallet);

  const load = useCallback(() => {
    void walletApi.withdrawals().then(setData);
    void auth.payoutProfile.get().then(setProfile).catch(() => setProfile({ exists: false }));
  }, []);

  useEffect(load, [load]);

  if (!data || !profile) return <Loading />;

  const min = Number(data.min_amount);
  const available = Number(balance.kyat_available ?? balance.kyat);
  const locked = Number(balance.kyat_locked ?? '0');
  const pct = min > 0 ? (available / min) * 100 : 0;
  const meetsMin = available >= min;

  async function request() {
    setBusy(true); setError(null); setNotice(null);
    const value = amount.trim() || available.toFixed(2);
    try {
      const res = await walletApi.requestWithdrawal(value);
      applyWallet(res.wallet);
      setNotice(
        res.status === 'approved'
          ? `Approved. ${value} Shwe Kyat is queued for payout.`
          : `Submitted for review. ${value} Shwe Kyat is now held.`,
      );
      setAmount('');
      load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'The request could not be submitted.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <ScreenBody>
      <ScreenHeader icon={<Icon name="kyat" size={22} />} title="Withdraw" onBack={onBack} />

      <Panel title="YOUR BALANCE">
        <p className="text-center font-pixel text-2xl text-kyat-glow tabular">{balance.kyat}</p>
        <p className="mt-1 text-center text-[11px] text-ink-dim">
          minimum withdrawal {data.min_amount} Shwe Kyat
        </p>
        <div className="mt-2.5"><Meter pct={pct} tone="kyat" label="Progress toward minimum withdrawal" /></div>

        <div className="mt-3">
          <StatGrid>
            <Stat k="Spendable" v={available.toFixed(2)} tone="kyat" />
            <Stat k="Held" v={locked.toFixed(2)} />
          </StatGrid>
        </div>
        {locked > 0 && (
          <p className="mt-2 text-[11px] leading-relaxed text-ink-dim">
            Held funds are still yours — they are reserved against a pending payout and
            cannot be spent until it settles.
          </p>
        )}
      </Panel>

      {error && <ErrorNote>{error}</ErrorNote>}
      {notice && (
        <p className="my-2 rounded-lg border border-[rgba(92,203,106,.4)] bg-[rgba(92,203,106,.1)]
                      px-3 py-2 text-xs text-[var(--ok)]">{notice}</p>
      )}

      {!flags.withdraw_enabled && (
        <Panel className="mt-3">
          <p className="text-[11px] leading-relaxed text-ink-dim">
            Withdrawals are not available on this account yet. Link a Google or Apple account
            or connect via Telegram (@shwetappybot) to verify your identity — a device-only guest
            account cannot be paid out.
          </p>
        </Panel>
      )}

      {profile.exists ? (
        <Panel title="PAYMENT PROFILE" className="mt-3">
          <StatGrid>
            <Stat k="Method" v={profile.method ?? '—'} />
            <Stat k="Account" v={profile.account_masked ?? '—'} />
          </StatGrid>
          <p className="mt-2.5 flex items-start gap-1.5 text-[11px] leading-relaxed text-torch-core">
            <Icon name="lock" size={13} className="mt-0.5 shrink-0" />
            <span>
              Locked after first setup. Changing it needs a support request and
              re-verification — unlimited edits is how payout fraud works.
            </span>
          </p>
        </Panel>
      ) : (
        <PayoutForm onSaved={load} disabled={!flags.withdraw_enabled} />
      )}

      {profile.exists && flags.withdraw_enabled && (
        <Panel title="REQUEST A PAYOUT" className="mt-3">
          <label className="block">
            <span className="text-[11px] text-ink-dim">
              Amount ({data.min_amount} – {data.max_amount} Shwe Kyat)
            </span>
            <input
              inputMode="decimal"
              value={amount}
              placeholder={available.toFixed(2)}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1 w-full rounded-lg border border-[var(--hairline)] bg-black/35 p-2.5 tabular"
            />
          </label>
          <Button
            variant="primary"
            className="mt-2.5 w-full"
            busy={busy}
            disabled={!meetsMin}
            onClick={() => void request()}
          >
            {meetsMin
              ? `Withdraw ${amount.trim() || available.toFixed(2)} Shwe Kyat`
              : `Need ${(min - available).toFixed(2)} more Shwe Kyat`}
          </Button>
          <p className="mt-2 text-[11px] text-ink-dim">
            Up to {data.max_per_week} requests per week.
          </p>
        </Panel>
      )}

      <Panel title="REQUESTS" className="mt-3">
        {data.results.length === 0 ? (
          <Empty>No withdrawals yet.</Empty>
        ) : (
          data.results.map((w) => (
            <Row
              key={w.uuid}
              icon={<Icon name={statusIcon(w.status)} style={{ color: statusColour(w.status) }} />}
              title={`${w.amount_kyat} Shwe Kyat`}
              subtitle={`${w.account_masked} · ${new Date(w.created_at).toLocaleDateString()}${
                w.status_reason ? ` · ${w.status_reason}` : ''}`}
              right={
                <span className="shrink-0 font-pixel text-[7px] uppercase"
                      style={{ color: statusColour(w.status) }}>
                  {w.status}
                </span>
              }
            />
          ))
        )}
      </Panel>
    </ScreenBody>
  );
}

function PayoutForm({ onSaved, disabled }: { onSaved: () => void; disabled: boolean }) {
  const [v, setV] = useState({
    country_code: 'MM', method: 'kpay', bank_name: '',
    account_name: '', account_number: '', phone: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const set = (k: keyof typeof v) => (e: { target: { value: string } }) =>
    setV((prev) => ({ ...prev, [k]: e.target.value }));

  async function save() {
    if (!v.account_name.trim() || !v.account_number.trim() || !v.phone.trim()) {
      setError('Fill in the account name, number and phone.');
      return;
    }
    setBusy(true); setError(null);
    try {
      await auth.payoutProfile.create(v);
      onSaved();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not save the profile.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <Panel title="PAYMENT PROFILE" className="mt-3">
      <p className="mb-2 text-[11px] leading-relaxed text-torch-core">
        One-time setup. Once saved this is locked — check it carefully.
      </p>

      <Field label="Country">
        <select value={v.country_code} onChange={set('country_code')} className={INPUT}>
          <option value="MM">Myanmar</option><option value="TH">Thailand</option>
          <option value="SG">Singapore</option><option value="JP">Japan</option>
        </select>
      </Field>
      <Field label="Payment method">
        <select value={v.method} onChange={set('method')} className={INPUT}>
          <option value="kpay">KBZPay (Myanmar)</option>
          <option value="wavepay">WavePay (Myanmar)</option>
          <option value="cbpay">CBPay (Myanmar)</option>
          <option value="ayapay">AYA Pay (Myanmar)</option>
          <option value="usdt_trc20">USDT (TRC-20 Crypto)</option>
          <option value="usdt_bep20">USDT (BEP-20 Crypto)</option>
          <option value="btc">Bitcoin (BTC Crypto)</option>
          <option value="ton">TON (Telegram Network)</option>
          <option value="bank">Bank Transfer</option>
          <option value="paypal">PayPal</option>
        </select>
      </Field>
      {v.method === 'bank' && (
        <Field label="Bank name">
          <input value={v.bank_name} onChange={set('bank_name')} className={INPUT} />
        </Field>
      )}
      <Field label="Account name">
        <input value={v.account_name} onChange={set('account_name')} className={INPUT} />
      </Field>
      <Field label="Account number">
        <input value={v.account_number} onChange={set('account_number')} inputMode="numeric"
               className={`${INPUT} tabular`} />
      </Field>
      <Field label="Phone number">
        <input value={v.phone} onChange={set('phone')} inputMode="tel" className={INPUT} />
      </Field>

      {error && <ErrorNote>{error}</ErrorNote>}

      <Button variant="primary" className="mt-1 w-full" busy={busy} disabled={disabled}
              onClick={() => void save()}>
        Confirm &amp; lock profile
      </Button>
    </Panel>
  );
}

const INPUT = 'mt-1 w-full rounded-lg border border-[var(--hairline)] bg-black/35 p-2.5';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="mb-2.5 block">
      <span className="text-[11px] text-ink-dim">{label}</span>
      {children}
    </label>
  );
}

const statusIcon = (s: string): IconName =>
  s === 'paid' ? 'check' : s === 'rejected' ? 'close' : s === 'cancelled' ? 'void' : 'hourglass';

const statusColour = (s: string): string =>
  s === 'paid' ? 'var(--ok)'
  : s === 'rejected' ? 'var(--danger)'
  : s === 'cancelled' ? 'var(--text-dim)'
  : 'var(--warn)';
